import React from 'react';
import { generatePath, Link } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import DeleteButton from '@/components/common/DeleteButton';
import EditButton from '@/components/common/EditButton';
import Loader from '@/components/common/Loader';
import PageSizeSelect from '@/components/common/PageSizeSelect';
import SearchInput from '@/components/common/SearchInput';
import ReportDataType from '@/constants/api/ReportDataType';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import DateType from '@/constants/DateType';
import FilterOperator from '@/constants/forms/FilterOperator';
import LabelResourceTypes from '@/constants/labels/LabelResourceTypes';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import { TODAY_API_FORMAT } from '@/lib/constants/dateConstants';
import translate from '@/lib/intl/translate';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { useQueryProvider } from '@/modules/QueryProvider';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { showErrorMessage, showSuccessMessage } from '@/utils/ToastFeedback';
import { useLabelManagementContext } from './LabelManagementContext';

function LabelList() {
  const { openDialog, closeDialog } = useDialogManager();
  const { query, updateQuery } = useQueryProvider();
  const { error, isLagging, isLoading, labels, remove, total, updateLabel } =
    useLabelManagementContext();

  const onPageChange = (e, value) => updateQuery({ page: value });
  const onPageSizeChange = (e) => updateQuery({ pageSize: e.target.value });
  const onSearchChange = (labelName) => updateQuery({ labelName });

  const handleDeleteDialog = (label) => {
    const { labelId, labelName: name } = label;
    openDialog(Dialogs.CONFIRMATION, {
      children: (
        <div>{translate('LABEL_DELETE_CONFIRM', { labelName: name })}</div>
      ),
      confirmLabel: translate('LABEL_DELETE'),
      onCancel: closeDialog,
      onClose: closeDialog,
      onConfirm: async () => {
        try {
          await remove(labelId);

          showSuccessMessage({
            message: translate('LABEL_DELETE_SUCCESS'),
          });
          closeDialog();
        } catch (err) {
          showErrorMessage({
            message: translate('LABEL_DELETE_ERROR'),
          });
        }
      },
    });
  };

  const handleEditDialog = (label) => {
    const { labelId } = label;
    openDialog(Dialogs.LABELS_EDIT, {
      label,
      onClose: closeDialog,
      onDelete: () => handleDeleteDialog(label),
      onUpdate: async (patch) => {
        try {
          await updateLabel(labelId, patch);

          showSuccessMessage({
            message: translate('LABEL_EDIT_SUCCESS'),
          });
          closeDialog();
        } catch (err) {
          if (err?.message === 'A label with that name already exists') {
            showErrorMessage({
              message: translate('LABEL_EDIT_ERROR_DUPLICATE', {
                labelName: patch.labelName,
              }),
            });
          } else {
            showErrorMessage({
              message: translate('LABEL_EDIT_ERROR'),
            });
          }
        }
      },
    });
  };

  const totalPages = Math.ceil((total || 0) / query.pageSize);

  if (error)
    return (
      <div className="mt-12 text-center">
        <div className="text-base">{translate('LABELS_ERROR_FETCHING')}</div>
      </div>
    );

  return (
    <div>
      <div className="flex items-center pt-5">
        <div>{translate('ALL_LABELS', { count: total })}</div>
        <PageSizeSelect
          value={query.pageSize}
          className="ml-auto"
          onChange={onPageSizeChange}
        />
      </div>
      <div>
        <SearchInput
          backgroundColor="#fff"
          onChange={onSearchChange}
          className="mb-8 w-3/4 md:w-1/2"
          label={translate('SEARCH')}
          placeholder={translate('SEARCH')}
          value={query.labelName || ''}
        />
        {labels && !labels.length && (
          <div className="mt-8 flex flex-col text-center">
            <div className="text-base">{translate('NO_LABELS')}</div>
            <hr className="mx-auto my-4 w-32 border-grey-600" />
            <div className="font-normal text-grey-900">
              {translate('LABELS_CREATE_ONE')}
            </div>
          </div>
        )}
        <div className={classNames({ 'opacity-60': isLagging })}>
          {labels?.map((label) => (
            <Tooltip
              enterDelay={500}
              enterNextDelay={500}
              title={label.labelName}
              key={label.labelId}
            >
              <div className="group -mx-1.5 flex items-center justify-between p-2 type-body1 hover:bg-grey-100">
                <div className="flex w-full md:w-11/12">
                  <div className="flex flex-col">
                    <div className="flex flex-row">{label.labelName}</div>
                    <div className="text-sm text-grey-900">
                      {label.usageCounts?.constituents ? (
                        <Link
                          className="text-tertiary-main"
                          to={{
                            pathname: generatePath(AuthRoutes.CONSTITUENTS),
                            search: joinNonEmptyParams({
                              filters: JSON.stringify([
                                {
                                  field:
                                    ConstituentSearchFilterField.CONSTITUENT_LABEL_IDS,
                                  operator: FilterOperator.ANY,
                                  value: [label.labelId],
                                },
                              ]),
                            }),
                          }}
                        >
                          {translate('CONSTITUENTS')}:{' '}
                          {label.usageCounts.constituents.toString()}
                        </Link>
                      ) : (
                        <>{translate('CONSTITUENTS')}: 0</>
                      )}
                      &nbsp;&bull;&nbsp;
                      {label.usageCounts?.donations ? (
                        <Link
                          className="text-tertiary-main"
                          to={{
                            pathname: generatePath(AuthRoutes.REPORTS),
                            search: joinNonEmptyParams({
                              dateType: DateType.CUSTOM_DATES,
                              endDate: TODAY_API_FORMAT,
                              labelIds: label.labelId,
                              labelResourceType: LabelResourceTypes.DON,
                              reportType: ReportDataType.DONATIONS,
                              startDate: '1900-01-01',
                            }),
                          }}
                        >
                          {translate('CONTRIBUTIONS')}:{' '}
                          {label.usageCounts.donations.toString()}
                        </Link>
                      ) : (
                        <>{translate('CONTRIBUTIONS')}: 0</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="invisible flex group-hover:visible">
                  <EditButton
                    className="mr-4 text-text-secondary"
                    iconClassName="text-base"
                    onClick={() => handleEditDialog(label)}
                  />
                  <DeleteButton
                    className="mr-4 text-text-secondary"
                    iconClassName="text-base"
                    onClick={() => handleDeleteDialog(label)}
                    size="small"
                  />
                </div>
              </div>
            </Tooltip>
          ))}
        </div>
        <div className="mt-6 flex flex-col items-center">
          {isLoading && <Loader isCentered className="my-12" />}
          {!!labels?.length && (
            <Pagination
              count={totalPages}
              page={query.page}
              onChange={onPageChange}
              shape="rounded"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default LabelList;
